import moment, { Moment } from 'moment'
import { OrgExpenseReport } from 'src/models/orgExpenseReports.models'

export enum DateRangeType {
  Last30Days = 'Last 30 Days',
  Today = 'Today',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  CurrentStatement = 'Current Statement',
  PreviousStatement = 'Previous Statement',
  Custom = 'Custom Date Range',
  Last90Days = 'Last 90 Days',
}

export type DateRangeTypeKey = keyof typeof DateRangeType

export interface DateRangeWindow {
  since: Moment
  until: Moment
}

export const getDateRangeTypeKey = (
  dateRangeType?: DateRangeType,
): DateRangeTypeKey | undefined => {
  if (!dateRangeType) return undefined

  return Object.entries(DateRangeType).find(
    ([, value]) => value === dateRangeType,
  )?.[0] as DateRangeTypeKey | undefined
}

export const getDateRangeTypeFromKey = (
  dateRangeTypeKey?: string,
): DateRangeType | undefined =>
  dateRangeTypeKey
    ? (DateRangeType as Record<string, DateRangeType | undefined>)[
        dateRangeTypeKey
      ]
    : undefined

export const resolveDateRangeTypeWindow = ({
  dateRangeType,
  now,
  currentStatement,
  previousStatement,
}: {
  dateRangeType: DateRangeType
  now?: Moment
  currentStatement?: OrgExpenseReport
  previousStatement?: OrgExpenseReport
}): DateRangeWindow | undefined => {
  const currentMoment = now?.clone() ?? moment()

  switch (dateRangeType) {
    case DateRangeType.Last30Days:
      return {
        since: currentMoment.clone().subtract(30, 'days').startOf('day'),
        until: currentMoment.clone().endOf('day'),
      }
    case DateRangeType.Today:
      return {
        since: currentMoment.clone().startOf('day'),
        until: currentMoment.clone().endOf('day'),
      }
    case DateRangeType.ThisMonth:
      return {
        since: currentMoment.clone().startOf('month'),
        until: currentMoment.clone().endOf('month'),
      }
    case DateRangeType.LastMonth: {
      const lastMonth = currentMoment.clone().subtract(1, 'month')
      return {
        since: lastMonth.clone().startOf('month'),
        until: lastMonth.clone().endOf('month'),
      }
    }
    case DateRangeType.Last90Days:
      return {
        since: currentMoment.clone().subtract(90, 'days').startOf('day'),
        until: currentMoment.clone().endOf('day'),
      }
    case DateRangeType.CurrentStatement:
      if (!currentStatement?.since || !currentStatement?.until) return undefined
      return {
        since: moment(currentStatement.since),
        until: moment(currentStatement.until),
      }
    case DateRangeType.PreviousStatement:
      if (!previousStatement?.since || !previousStatement?.until) return undefined
      return {
        since: moment(previousStatement.since),
        until: moment(previousStatement.until),
      }
    case DateRangeType.Custom:
      return undefined
  }
}
